import React, { useState } from 'react';
import { User, Mail, Lock, Phone, CreditCard, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClienteAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    telefono: '',
    cedula: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isLogin ? '/api/cliente/login' : '/api/cliente/registro';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      
      if (response.data.token) {
        localStorage.setItem('sendhapy_token', response.data.token);
        localStorage.setItem('sendhapy_cliente', JSON.stringify(response.data.cliente));
        navigate('/mi-cuenta');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error en la autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" 
         style={{ background: 'linear-gradient(135deg, var(--sendhapy-teal) 0%, var(--sendhapy-teal-dark) 100%)' }}>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/sendhapy-logo-yellow.png" 
            alt="SendHapy" 
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-white">
            {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
          </h1>
          <p className="text-white/80 mt-2">
            {isLogin ? 'Accede a tu portal de cliente' : 'Regístrate para obtener tu casillero'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="nombre"
                data-testid="cliente-auth-nombre-input"
                    placeholder="Nombre completo"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    name="telefono"
                data-testid="cliente-auth-telefono-input"
                    placeholder="Teléfono/WhatsApp"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="cedula"
                data-testid="cliente-auth-cedula-input"
                    placeholder="Cédula"
                    value={formData.cedula}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                name="email"
                data-testid="cliente-auth-email-input"
                placeholder="Correo electrónico"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                data-testid="cliente-auth-password-input"
                placeholder="Contraseña"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {isLogin ? (
              <div className="text-right -mt-2">
                <a href="/recuperar" data-testid="forgot-password-link" className="text-sm font-medium hover:underline" style={{ color: 'var(--sendhapy-teal)' }}>¿Olvidaste tu contraseña?</a>
              </div>
            ) : (
              <label className="flex items-start gap-2 text-sm text-gray-600">
                <input type="checkbox" data-testid="cliente-auth-whatsapp-checkbox" checked={!!formData.acepta_whatsapp}
                  onChange={(e) => setFormData({ ...formData, acepta_whatsapp: e.target.checked })} className="mt-1 accent-teal-700" />
                Acepto recibir mensajes de SendHapy por WhatsApp sobre mi casillero y mis paquetes.
              </label>
            )}

            <button
              type="submit"
              data-testid="cliente-auth-submit-button"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-lg transition-all disabled:opacity-50"
              style={{ background: 'var(--sendhapy-yellow)', color: 'var(--sendhapy-teal)' }}
            >
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-teal-600 hover:underline font-medium"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <a href="/" className="text-gray-500 hover:text-gray-700 text-sm">
              ← Volver al inicio
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClienteAuth;
